import { CreateRelationshipsLinkArgs } from '../../../relationshipDiagrams/types';
import type { RelationshipsLink, RelationshipsLinkCssColors } from '../types';
import {
  DEFAULT_EDGE_COLOR_ATTRIBUTES,
  PARENT_CHILD_LINE_TYPE,
  processSimilarLinks,
  isAggregatedGraphEdge,
  isParentChildGraphEdge,
  type PureGraphEdge,
} from '@ardoq/graph';

import { PARENT_CHILD_REFERENCE } from '@ardoq/global-consts';
import { lineTypeDashArrays } from 'tabview/canvasRendering/consts';
import { ArdoqId } from '@ardoq/api-types';

const getEdgeCssColors = (
  graphEdge: PureGraphEdge
): RelationshipsLinkCssColors => {
  if (isParentChildGraphEdge(graphEdge)) {
    return DEFAULT_EDGE_COLOR_ATTRIBUTES;
  }
  const { fill, stroke } = graphEdge.getCssColorAttributes();
  return {
    fill: fill ?? DEFAULT_EDGE_COLOR_ATTRIBUTES.fill,
    stroke: stroke ?? DEFAULT_EDGE_COLOR_ATTRIBUTES.stroke,
  };
};

const getGlobalTypeIdFromEdge = (graphEdge: PureGraphEdge): string | null =>
  isParentChildGraphEdge(graphEdge) || graphEdge.isParentChildReference
    ? PARENT_CHILD_REFERENCE
    : graphEdge.getGlobalTypeId();

const getMatchingFilterIds = (
  graphEdge: PureGraphEdge,
  colorFilterIdsByReferenceId: Map<ArdoqId, string[]>
): string[] | null => {
  if (isAggregatedGraphEdge(graphEdge)) {
    const filterIds = [
      ...new Set(
        graphEdge.modelIds.flatMap(
          id => colorFilterIdsByReferenceId.get(id) ?? []
        )
      ),
    ];
    return filterIds.length ? filterIds : null;
  }
  const { modelId } = graphEdge;
  return modelId ? (colorFilterIdsByReferenceId.get(modelId) ?? null) : null;
};

const createLink = ({
  graphEdge,
  nodeMap,
  referenceType,
  linksBySourceAndTarget,
  colorFilterIdsByReferenceId,
}: CreateRelationshipsLinkArgs): RelationshipsLink => {
  const { modelId } = graphEdge;

  const label = graphEdge.getItemLabels()?.mainLabel ?? '';

  const dashArray =
    lineTypeDashArrays[referenceType?.line ?? PARENT_CHILD_LINE_TYPE];
  const link: RelationshipsLink = {
    source: nodeMap.get(graphEdge.source)!,
    target: nodeMap.get(graphEdge.target)!,
    lineBeginning: graphEdge.getLineBeginning(),
    lineEnding: graphEdge.getLineEnding(),
    modelId,
    dashArray,
    similarLinksIndex: 0,
    similarLinksCount: 1,
    globalTypeId: getGlobalTypeIdFromEdge(graphEdge),
    label,
    matchingFilterIds: getMatchingFilterIds(
      graphEdge,
      colorFilterIdsByReferenceId
    ),
    cssColors: getEdgeCssColors(graphEdge),
  };

  processSimilarLinks(link, linksBySourceAndTarget);
  return link;
};

export { createLink };
